import { useState } from 'react'
import { Send, FlaskConical, Users as UsersIcon, Clock, AlertTriangle, CreditCard } from 'lucide-react'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { Card, daysLeft } from '../ui'
import { timeAgo } from '../../utils/timeAgo'

const MERGE_FIELDS = ['{{admin_name}}', '{{org_name}}', '{{trial_end}}', '{{plan}}']

const SEGMENTS = [
  {
    key: 'all_admins', label: 'All admins', icon: UsersIcon,
    hint: 'Every active org admin',
    orgTest: () => true,
  },
  {
    key: 'trial_ending', label: 'Trial ending', icon: Clock,
    hint: 'Trials with ≤7 days left',
    orgTest: (o) => o.subscriptionStatus === 'trialing' && o.trialEndsAt && daysLeft(o.trialEndsAt) >= 0 && daysLeft(o.trialEndsAt) <= 7,
  },
  {
    key: 'trial_expired', label: 'Trial expired', icon: AlertTriangle,
    hint: 'Locked out, no payment yet',
    orgTest: (o) => o.subscriptionStatus === 'trialing' && o.trialEndsAt && daysLeft(o.trialEndsAt) < 0,
  },
  {
    key: 'past_due', label: 'Past due', icon: CreditCard,
    hint: 'Payment failed',
    orgTest: (o) => o.subscriptionStatus === 'past_due',
  },
]

const SEGMENT_LABEL = Object.fromEntries(SEGMENTS.map((s) => [s.key, s.label]))

const TEMPLATES = {
  trial_ending: {
    subject: 'Your PetroTrack trial ends on {{trial_end}}',
    body: 'Hi {{admin_name}},\n\nYour {{plan}} trial for {{org_name}} ends on {{trial_end}}. Subscribe from the Billing page to keep your daily entries, reports and team access running without interruption.\n\nNeed help deciding or want a walkthrough? Just reply to this email.\n\n— The PetroTrack team',
  },
  trial_expired: {
    subject: 'Your PetroTrack trial for {{org_name}} has ended',
    body: 'Hi {{admin_name}},\n\nYour trial for {{org_name}} has ended and access is paused — your data is safe and exactly where you left it. Subscribe from the Billing page to pick up where you stopped.\n\nIf you need more time to evaluate, reply and we can extend your trial.\n\n— The PetroTrack team',
  },
  past_due: {
    subject: 'Payment issue on your PetroTrack subscription',
    body: 'Hi {{admin_name}},\n\nThe latest payment for {{org_name}} ({{plan}} plan) didn\'t go through, so your subscription is past due. Please update your card on the Billing page to avoid interruption.\n\nReply if you think this is a mistake.\n\n— The PetroTrack team',
  },
}

// Counts a segment's recipients client-side for display; the send-broadcast
// function recomputes the authoritative list server-side.
function segmentCount(seg, organizations, users) {
  const orgIds = new Set(organizations.filter(seg.orgTest).map((o) => o.id))
  return users.filter((u) => u.role === 'admin' && u.isActive !== false && orgIds.has(u.organizationId)).length
}

export default function Email({ organizations, users, currentUser, broadcasts, showToast }) {
  const { history, sendBroadcast } = broadcasts
  const [segment, setSegment] = useState('all_admins')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false) // false | 'test' | 'send'
  const [confirming, setConfirming] = useState(false)

  const seg = SEGMENTS.find((s) => s.key === segment)
  const count = segmentCount(seg, organizations, users)
  const canCompose = subject.trim() && body.trim()

  function pickSegment(key) {
    setSegment(key)
    // Prefill an editable starting point when the composer is still empty.
    if (!subject.trim() && !body.trim() && TEMPLATES[key]) {
      setSubject(TEMPLATES[key].subject)
      setBody(TEMPLATES[key].body)
    }
  }

  async function handleTest() {
    setBusy('test')
    const result = await sendBroadcast({ segment, subject, body, testTo: currentUser.email })
    setBusy(false)
    showToast(result.success ? `Test sent to ${currentUser.email} — check your inbox.` : result.message,
      result.success ? 'success' : 'error')
  }

  async function handleSend() {
    setConfirming(false)
    setBusy('send')
    const result = await sendBroadcast({ segment, subject, body })
    setBusy(false)
    if (result.success) {
      showToast(`Sent to ${result.sent} admin(s)${result.failed ? ` — ${result.failed} failed` : ''}.`,
        result.failed ? 'error' : 'success')
      setSubject(''); setBody('')
    } else {
      showToast(result.message, 'error')
    }
  }

  return (
    <div className="space-y-4">
      {/* Segments */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SEGMENTS.map((s) => {
          const Icon = s.icon
          const n = segmentCount(s, organizations, users)
          const active = segment === s.key
          return (
            <button key={s.key} onClick={() => pickSegment(s.key)}
              className={`text-left bg-white rounded-xl border px-4 py-3.5 transition ${
                active ? 'border-teal-500 ring-2 ring-teal-100' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <div className="flex items-center justify-between">
                <Icon className={`w-4 h-4 ${active ? 'text-teal-600' : 'text-gray-400'}`} />
                <span className={`text-lg font-bold tabular-nums ${active ? 'text-teal-700' : 'text-gray-800'}`}>{n}</span>
              </div>
              <p className="text-sm font-semibold text-gray-800 mt-1.5">{s.label}</p>
              <p className="text-[11px] text-gray-400">{s.hint}</p>
            </button>
          )
        })}
      </div>

      {/* Composer */}
      <Card title={`Compose — ${seg.label} (${count} recipient${count !== 1 ? 's' : ''})`}>
        <div className="px-4 py-4 space-y-3">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
            placeholder={'Hi {{admin_name}},\n\nWrite your message…'}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-y font-mono" />
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-gray-400 mr-1">Merge fields (click to insert):</span>
            {MERGE_FIELDS.map((f) => (
              <button key={f} onClick={() => setBody((b) => b + f)}
                className="text-[11px] font-mono bg-gray-100 hover:bg-teal-50 hover:text-teal-700 text-gray-600 rounded px-1.5 py-0.5 transition">
                {f}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
            <button onClick={handleTest} disabled={!canCompose || !!busy}
              className="flex items-center justify-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition">
              <FlaskConical className="w-3.5 h-3.5" />
              {busy === 'test' ? 'Sending test…' : `Send test to me`}
            </button>
            <button onClick={() => setConfirming(true)} disabled={!canCompose || !!busy || count === 0}
              className="flex items-center justify-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 transition">
              <Send className="w-3.5 h-3.5" />
              {busy === 'send' ? 'Sending…' : `Send to ${count} admin${count !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </Card>

      {/* History */}
      <Card title="Sent broadcasts">
        {history.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">
            Nothing sent yet. Broadcasts you send appear here.
          </p>
        ) : (
          <ul>
            {history.map((b) => (
              <li key={b.id} className="px-4 py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-sm font-semibold text-gray-800 truncate">{b.subject}</p>
                  <span className="text-[11px] text-gray-400 flex-none">{timeAgo(b.createdAt)}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {SEGMENT_LABEL[b.segment] || b.segment} · {b.recipientCount} delivered
                  {b.failedCount > 0 && <span className="text-red-500"> · {b.failedCount} failed</span>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {confirming && (
        <ConfirmDialog
          message={`Send "${subject.trim()}" to ${count} admin(s) in "${seg.label}"? This emails real customers and cannot be recalled.`}
          onConfirm={handleSend}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  )
}
